import { useState } from 'react';
import { toast } from 'sonner';
import { GraduationCap, Plus, Pencil, Archive, Copy, UserPlus, Briefcase } from 'lucide-react';
import { ApiError } from '../../lib/api';
import {
  useTrainingModules,
  useCreateTrainingModule,
  useCopyTrainingModule,
  useArchiveTrainingModule,
  useSetTrainingRecurrence,
  useSetTrainingAssignAll,
  type TrainingModuleSummary,
} from '../../hooks/useTrainingModules';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardSection } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Pill } from '../../components/ui/Pill';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonList } from '../../components/ui/Skeleton';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { ModuleEditor } from '../../components/training/builder/ModuleEditor';
import { AssignPeopleModal } from '../../components/training/AssignPeopleModal';
import { TargetRolesModal } from '../../components/training/TargetRolesModal';

function slug(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

function currentVersion(m: TrainingModuleSummary) {
  if (m.versions.length === 0) return null;
  return [...m.versions].sort((a, b) => b.version - a.version)[0]!;
}

/** Admin list + builder for e-learning training modules. */
export function TrainingModules() {
  const list = useTrainingModules();
  const create = useCreateTrainingModule();
  const copy = useCopyTrainingModule();
  const archive = useArchiveTrainingModule();

  const [title, setTitle] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmArchive, setConfirmArchive] = useState<TrainingModuleSummary | null>(null);
  const [assignModule, setAssignModule] = useState<TrainingModuleSummary | null>(null);
  const [targetModule, setTargetModule] = useState<TrainingModuleSummary | null>(null);

  const modules = (list.data ?? []).filter((m) => !m.archived_at);
  const editing = modules.find((m) => m.id === editingId) ?? null;

  async function onCreate() {
    if (!title.trim()) return;
    try {
      const res = await create.mutateAsync({ key: slug(title), title: title.trim() });
      setTitle('');
      toast.success('Module created');
      setEditingId(res.module.id);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not create module');
    }
  }

  async function onCopy(m: TrainingModuleSummary) {
    try {
      const res = await copy.mutateAsync(m.id);
      toast.success('Module copied — editing the draft');
      setEditingId(res.module.id);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not copy module');
    }
  }

  async function onArchive(m: TrainingModuleSummary) {
    try {
      await archive.mutateAsync(m.id);
      toast.success('Module archived');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not archive');
    }
    setConfirmArchive(null);
  }

  return (
    <main className={`mx-auto space-y-5 px-4 py-8 ${editing ? 'max-w-screen-2xl' : 'max-w-5xl'}`}>
      <PageHeader
        eyebrowIcon={GraduationCap}
        eyebrow="Training"
        title={editing ? editing.title : 'Training modules'}
        description={
          editing
            ? `Editing "${editing.key}". Publish to create a new version.`
            : 'Build e-learning modules: slides, quiz questions, a pass mark and a certificate on completion.'
        }
      />

      {editing ? (
        <ModuleEditor module={editing} onBack={() => setEditingId(null)} />
      ) : (
        <>
          <Card>
            <CardSection className="flex flex-wrap items-end gap-2">
              <label className="flex-1 text-sm">
                <span className="font-medium text-fg-muted">New module title</span>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Trips, Slips and Falls"
                  className="mt-1 block w-full rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm"
                />
              </label>
              <Button
                variant="primary"
                size="sm"
                iconLeft={<Plus className="h-4 w-4" />}
                onClick={() => void onCreate()}
                loading={create.isPending}
                disabled={!title.trim()}
              >
                Create
              </Button>
            </CardSection>
          </Card>

          {list.isLoading ? (
            <SkeletonList rows={3} />
          ) : modules.length === 0 ? (
            <EmptyState
              icon={<GraduationCap className="h-6 w-6" />}
              title="No training modules yet"
              description="Create your first module above, then add it to a role template or assign it per person."
            />
          ) : (
            <div className="space-y-3">
              {modules.map((m) => {
                const v = currentVersion(m);
                return (
                  <Card key={m.id}>
                    <CardSection>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate font-medium text-fg">{m.title}</div>
                          <div className="mt-1 flex flex-wrap items-center gap-1">
                            <Pill tone="neutral" size="sm">
                              {m.key}
                            </Pill>
                            {v ? (
                              <>
                                <Pill tone="success" size="sm">
                                  v{v.version} published
                                </Pill>
                                {v.kind === 'external' ? (
                                  <Pill tone="accent" size="sm">
                                    External link
                                  </Pill>
                                ) : v.graded ? (
                                  <>
                                    <Pill tone="info" size="sm">
                                      Pass mark {v.pass_mark}%
                                    </Pill>
                                    <Pill tone="accent" size="sm">
                                      {v.retry_mode === 'try_again'
                                        ? 'Try again per question'
                                        : 'Full retake'}
                                    </Pill>
                                  </>
                                ) : null}
                              </>
                            ) : (
                              <Pill tone="warning" size="sm">
                                Draft — no version
                              </Pill>
                            )}
                            {m.recurrence_months ? (
                              <Pill tone="info" size="sm">
                                Repeats every {m.recurrence_months} mo
                              </Pill>
                            ) : null}
                            {m.assign_to_all ? (
                              <Pill tone="accent" size="sm">
                                {m.assign_to_all_scope === 'new_starters'
                                  ? 'Everyone (new starters)'
                                  : 'Everyone'}
                              </Pill>
                            ) : null}
                            {v && !v.graded ? (
                              <Pill tone="info" size="sm">
                                Not marked
                              </Pill>
                            ) : null}
                            {m.notify_on_completion && m.notify_emails.length > 0 ? (
                              <Pill tone="accent" size="sm">
                                Emails answers ({m.notify_emails.length})
                              </Pill>
                            ) : null}
                            {m.draft ? (
                              <Pill tone="warning" size="sm">
                                Unpublished edits
                              </Pill>
                            ) : null}
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          <AssignAllToggle module={m} />
                          <RecurrenceSelect module={m} />
                          {v ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              iconLeft={<UserPlus className="h-4 w-4" />}
                              onClick={() => setAssignModule(m)}
                            >
                              Assign
                            </Button>
                          ) : null}
                          <Button
                            variant="ghost"
                            size="sm"
                            iconLeft={<Briefcase className="h-4 w-4" />}
                            onClick={() => setTargetModule(m)}
                          >
                            Audience
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            iconLeft={<Pencil className="h-4 w-4" />}
                            onClick={() => setEditingId(m.id)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            iconLeft={<Copy className="h-4 w-4" />}
                            onClick={() => void onCopy(m)}
                            loading={copy.isPending}
                          >
                            Copy
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            iconLeft={<Archive className="h-4 w-4" />}
                            onClick={() => setConfirmArchive(m)}
                          >
                            Archive
                          </Button>
                        </div>
                      </div>
                    </CardSection>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      <ConfirmModal
        open={!!confirmArchive}
        title="Archive module?"
        message={
          confirmArchive
            ? `"${confirmArchive.title}" will be hidden from assignment. Existing assignments keep working.`
            : ''
        }
        tone="warning"
        confirmLabel="Archive"
        onConfirm={() => {
          if (confirmArchive) return onArchive(confirmArchive);
        }}
        onClose={() => setConfirmArchive(null)}
      />

      {assignModule ? (
        <AssignPeopleModal
          module={assignModule}
          open={!!assignModule}
          onClose={() => setAssignModule(null)}
        />
      ) : null}

      {targetModule ? (
        <TargetRolesModal
          module={targetModule}
          open={!!targetModule}
          onClose={() => setTargetModule(null)}
        />
      ) : null}
    </main>
  );
}

const RECURRENCE_OPTIONS: { label: string; value: number | null }[] = [
  { label: 'One-off', value: null },
  { label: 'Every 6 months', value: 6 },
  { label: 'Every 12 months', value: 12 },
  { label: 'Every 24 months', value: 24 },
  { label: 'Every 36 months', value: 36 },
];

/**
 * Toggles "available to everyone". When on, the daily training sweep assigns
 * the module to every training person and each new learner. A plain checkbox:
 * the heavy lifting is the sweep, this just flips the flag.
 */
function AssignAllToggle({ module }: { module: TrainingModuleSummary }) {
  const setAll = useSetTrainingAssignAll();
  const current = !module.assign_to_all
    ? 'off'
    : module.assign_to_all_scope === 'new_starters'
      ? 'new'
      : 'all';
  return (
    <select
      value={current}
      disabled={setAll.isPending}
      aria-label="Available to everyone"
      title="Auto-assign this module. 'New starters' skips existing staff."
      onChange={(e) => {
        const v = e.target.value;
        const input =
          v === 'off'
            ? { id: module.id, assign_to_all: false }
            : {
                id: module.id,
                assign_to_all: true,
                assign_to_all_scope: v === 'new' ? ('new_starters' as const) : ('all' as const),
              };
        setAll.mutate(input, {
          onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not update'),
        });
      }}
      className="rounded-token-sm border border-line-strong/20 bg-surface px-2 py-1 text-xs text-fg"
    >
      <option value="off">Not everyone</option>
      <option value="all">Everyone</option>
      <option value="new">Everyone (new starters)</option>
    </select>
  );
}

/**
 * Sets how often a module must be retaken. Saved straight to the module (a
 * recurrence policy, not version content) so it never mints a new version.
 */
function RecurrenceSelect({ module }: { module: TrainingModuleSummary }) {
  const setRecurrence = useSetTrainingRecurrence();
  return (
    <label className="flex items-center gap-1.5 text-xs text-fg-muted">
      <span className="sr-only">Recurrence for {module.title}</span>
      <select
        value={module.recurrence_months ?? ''}
        disabled={setRecurrence.isPending}
        onChange={(e) => {
          const value = e.target.value === '' ? null : Number(e.target.value);
          setRecurrence.mutate(
            { id: module.id, recurrence_months: value },
            {
              onError: (err) =>
                toast.error(err instanceof ApiError ? err.message : 'Could not update recurrence'),
            },
          );
        }}
        className="rounded-token-sm border border-line-strong/15 bg-surface px-2 py-1 text-xs text-fg"
        title="How often this module must be retaken"
      >
        {RECURRENCE_OPTIONS.map((o) => (
          <option key={o.label} value={o.value ?? ''}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
